import { Component, DestroyRef, inject, Inject, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { OKTA_AUTH } from '@okta/okta-angular';
import { OktaAuth } from '@okta/okta-auth-js';
import { Popover } from 'primeng/popover';
import { ButtonModule } from 'primeng/button';
import { DialogModule } from 'primeng/dialog';
import { AuthRoleService } from '@services/auth-role.service';
import { Team } from '@models/team.model';
import { CmrDataFacade } from '../../../modules/cmr/services/cmr-data-facade.service';
import { CmrSubscriptionService } from '../../../modules/cmr/services/cmr-subscription.service';
import { CmrSubscription } from '../../../modules/cmr/models/cmr-subscription.model';

@Component({
  selector: 'glass-badge',
  templateUrl: './badge.component.html',
  styleUrls: ['./badge.component.css'],
  standalone: true,
  imports: [CommonModule, Popover, ButtonModule, DialogModule],
})
export class BadgeComponent implements OnInit {
  initials = '';
  fullName = '';
  email = '';
  userTeams: Team[] = [];
  isAdmin$ = inject(AuthRoleService).isAdmin$;

  // Subscriptions
  showSubscriptionsModal = false;
  isLoadingSubscriptions = false;

  get subscriptions() { return this.subscriptionService.snapshot; }
  get subscribedCmrs() { return this.subscriptions.filter(s => s.type === 'cmr'); }
  get subscribedTeams() { return this.subscriptions.filter(s => s.type === 'team'); }
  get subscribedComponents() { return this.subscriptions.filter(s => s.type === 'component'); }

  private subscriptionService = inject(CmrSubscriptionService);

  get userOrgs(): string[] {
    return [...new Set(this.userTeams.map((t) => t.org).filter((o): o is string => !!o))];
  }

  private facade = inject(CmrDataFacade);
  private destroyRef = inject(DestroyRef);

  constructor(
    @Inject(OKTA_AUTH) private auth: OktaAuth,
  ) {}

  ngOnInit() {
    this.auth.getUser().then(
      (claims: any) => {
        this.initials = this.getInitials(claims);
        this.fullName = this.normalizeName(claims.name) || claims.given_name || '';
        this.email = claims.email || '';
        this.loadTeams();
        this.subscriptionService.load(this.email);
      },
      (error: any) => {
        console.error('Failed to get user from Okta', error);
      },
    );
  }

  /**
   * The user's active teams, for the "My teams / My orgs" popover.
   *
   * Reads through {@link CmrDataFacade} so the navbar doesn't pull the full team list — the
   * heaviest documents in the system, Slack and Jira templates included — on every app load;
   * whichever of the badge or the first CMR page loads first pays for both. `teamsForUser`
   * also matches on normalized email, which a raw `===` against the Okta claim does not:
   * stored member emails are lowercased by the API, so a mixed-case claim matched nothing.
   */
  private loadTeams(): void {
    this.facade
      .teamsForUser(this.email)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((teams: Team[]) => {
        this.userTeams = teams;
      });
  }

  openSubscriptions(): void {
    this.showSubscriptionsModal = true;
  }

  unsubscribe(sub: CmrSubscription): void {
    if (!sub._id) return;
    this.subscriptionService.unsubscribe(sub._id, this.email).subscribe();
  }

  private normalizeName(name: string | undefined): string {
    if (!name) return '';
    if (name.includes(',')) {
      const [last, first] = name.split(',').map((p) => p.trim());
      return `${first} ${last}`;
    }
    return name;
  }

  getInitials(user: any): string {
    if (!user) return '';
    const firstInitial = (user.firstName || user.given_name || ' ')[0].toUpperCase();
    const lastInitial = (user.lastName || user.family_name || ' ')[0].toUpperCase();
    return `${firstInitial}${lastInitial}`.trim();
  }

  async logout(): Promise<void> {
    await this.auth.signOut();
  }
}
